import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { subscribeToNewsletter } from '../services/newsletterService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useNewsletterSubscribe() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);

  const resetFeedback = useCallback(() => {
    setMessage(null);
    setMessageType(null);
  }, []);

  const handleEmailChange = useCallback(
    (value) => {
      setEmail(value);
      resetFeedback();
    },
    [resetFeedback]
  );

  const submit = useCallback(
    async (emailOverride) => {
      const value = (emailOverride ?? email).trim();
      resetFeedback();

      if (!value || !EMAIL_RE.test(value)) {
        setMessage(t('newsletter.error'));
        setMessageType('error');
        return { success: false };
      }

      setIsSubmitting(true);
      try {
        const result = await subscribeToNewsletter(value);
        if (result.success) {
          setMessage(result.message);
          setMessageType('success');
          setEmail('');
        } else {
          setMessage(result.message);
          setMessageType('error');
        }
        return result;
      } catch {
        setMessage(t('newsletter.error'));
        setMessageType('error');
        return { success: false };
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, resetFeedback, t]
  );

  const reset = useCallback(() => {
    setEmail('');
    resetFeedback();
    setIsSubmitting(false);
  }, [resetFeedback]);

  return {
    email,
    setEmail: handleEmailChange,
    isSubmitting,
    message,
    messageType,
    submit,
    reset,
  };
}
